import {
  FEATURE_FLAG_DEFS,
  FeatureFlagsResponse,
  type FeatureFlag,
  type FeatureFlagsResponse as FeatureFlagsResponseType,
} from '@uit/shared/schemas/feature-flags';
import { useState } from 'react';
import { apiMutation } from '../../lib/api.js';
import { useFeatureFlags } from '../../lib/feature-flags.js';
import s from '../../pages/SettingsPage.module.scss';

/**
 * Feature flags (T-152) — admin toggles for the gated Phase-2 features. Turning
 * one on exposes it without a redeploy. Everything defaults OFF, so a fresh
 * prod deploy ships dark until an admin flips a feature on here.
 */
export function FeatureFlagsSection() {
  const { flags, refetch } = useFeatureFlags();
  const [savingKey, setSavingKey] = useState<FeatureFlag | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (key: FeatureFlag, next: boolean): Promise<void> => {
    setSavingKey(key);
    setError(null);
    try {
      await apiMutation<Partial<Record<FeatureFlag, boolean>>, FeatureFlagsResponseType>(
        '/api/settings/feature-flags',
        { [key]: next },
        { method: 'PATCH', schema: FeatureFlagsResponse },
      );
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message.includes('403') ? 'You do not have permission.' : 'Could not save.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Features</h2>
      <p className={s.sectionSubtitle}>
        Turn gated features on once they’ve been tested. Off by default — a feature stays hidden
        until enabled here (no redeploy needed).
      </p>

      {FEATURE_FLAG_DEFS.map((def) => (
        <div key={def.key} className={s.field}>
          <label className={s.label}>
            <input
              type="checkbox"
              checked={flags[def.key]}
              disabled={savingKey !== null}
              onChange={(e) => {
                void toggle(def.key, e.target.checked);
              }}
            />{' '}
            {def.label}
            {flags[def.key] ? ' — on' : ' — off'}
          </label>
          <p className={s.sectionSubtitle}>{def.description}</p>
        </div>
      ))}

      {error && (
        <div className={s.error} role="alert">
          {error}
        </div>
      )}
    </section>
  );
}
